import { useState } from 'react'
import { FileText, Play, Book, Download, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react'
import Header from '../components/Header'
import Footer from '../components/Footer'

interface ResourceItem {
  id: string
  name: string
  href: string
  type: 'document' | 'video' | 'other'
}

interface ResourceCategory {
  id: string
  name: string
  icon: React.ElementType
  items: ResourceItem[]
}

const resources: ResourceCategory[] = [
  {
    id: 'manuals',
    name: '说明书',
    icon: FileText,
    items: [
      { id: 'air-heater-manual', name: '风暖炉使用说明书', href: '/air-heater-manual', type: 'document' },
      { id: 'water-heater-manual', name: '水暖炉使用说明书', href: '/water-heater-manual', type: 'document' },
      { id: 'burner-manual', name: '燃烧机使用说明书', href: '/burner-manual', type: 'document' },
      { id: 'wood-fireplace-manual', name: '燃木壁炉使用说明书', href: '/wood-fireplace-manual', type: 'document' },
      { id: 'troubleshooting-manual', name: '故障排查和维修手册', href: '/troubleshooting-manual', type: 'document' },
    ],
  },
  {
    id: 'operation-videos',
    name: '操作视频',
    icon: Play,
    items: [
      { id: 'air-heater-operation', name: '风暖炉操作视频', href: '#', type: 'video' },
      { id: 'water-heater-operation', name: '水暖炉操作视频', href: '#', type: 'video' },
      { id: 'burner-operation', name: '燃烧机操作视频', href: '#', type: 'video' },
      { id: 'wood-fireplace-operation', name: '燃木壁炉操作视频', href: '#', type: 'video' },
    ],
  },
  {
    id: 'maintenance-videos',
    name: '维修视频',
    icon: Play,
    items: [
      { id: 'air-heater-maintenance', name: '风暖炉维修视频', href: '/air-heater-maintenance-videos', type: 'video' },
      { id: 'water-heater-maintenance', name: '水暖炉维修视频', href: '#', type: 'video' },
      { id: 'burner-maintenance', name: '燃烧机维修视频', href: '#', type: 'video' },
      { id: 'wood-fireplace-maintenance', name: '燃木壁炉维修视频', href: '#', type: 'video' },
    ],
  },
  {
    id: 'technical-documents',
    name: '技术文档',
    icon: Book,
    items: [
      { id: 'product-specs', name: '产品规格书', href: '#', type: 'document' },
      { id: 'installation-guide', name: '安装指南', href: '#', type: 'document' },
      { id: 'safety-manual', name: '安全手册', href: '#', type: 'document' },
      { id: 'warranty-policy', name: ' warranty policy', href: '#', type: 'document' },
    ],
  },
]

export default function ResourcesPage() {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)

  const toggleCategory = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId)
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-700 to-primary-900 text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent-orange/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-4">
              资料中心
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              产品资料与技术支持
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              在这里您可以找到我们产品的使用说明书、操作视频、维修视频和技术文档，为您提供全方位的技术支持
            </p>
          </div>
        </div>
      </section>



      {/* Resources Section */}
      <section className="py-20 lg:py-28 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {resources.map((category) => (
              <div key={category.id} className="bg-white rounded-2xl shadow-sm border border-secondary-100 overflow-hidden">
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-secondary-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-primary-50">
                      <category.icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-secondary-900">{category.name}</h3>
                  </div>
                  {expandedCategory === category.id ? (
                    <ChevronDown className="w-5 h-5 text-secondary-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-secondary-500" />
                  )}
                </button>
                
                {expandedCategory === category.id && (
                  <div className="px-6 py-4 border-t border-secondary-100">
                    <div className="space-y-3">
                      {category.items.map((item) => (
                        <a
                          key={item.id}
                          href={item.href}
                          className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary-50 transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-secondary-100">
                              {item.type === 'document' ? (
                                <FileText className="w-5 h-5 text-secondary-600" />
                              ) : item.type === 'video' ? (
                                <Play className="w-5 h-5 text-secondary-600" />
                              ) : (
                                <Download className="w-5 h-5 text-secondary-600" />
                              )}
                            </div>
                            <span className="text-secondary-900 group-hover:text-primary-600 transition-colors">
                              {item.name}
                            </span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-secondary-400 group-hover:text-primary-600 transition-colors" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary-50 rounded-3xl p-8 lg:p-12 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-4">
              需要更多技术支持？
            </h2>
            <p className="text-secondary-600 mb-8 max-w-2xl mx-auto">
              如果您在使用我们的产品时遇到任何问题，或者需要更多的技术文档，请随时联系我们的技术支持团队
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
            >
              联系技术支持
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}